import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authAPI } from '../utils/api';

export default function Register() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', email: '', employeeId: '',
    designation: '', phone: '', password: '', confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const handleChange = e => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) setErrors(er => ({ ...er, [e.target.name]: '' }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim() || form.name.length < 2) errs.name = 'Full name required (min 2 chars)';
    if (!form.email || !/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Valid email required';
    if (!form.employeeId.trim()) errs.employeeId = 'Employee ID required';
    if (!form.password || form.password.length < 8) errs.password = 'Min 8 characters';
    else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(form.password))
      errs.password = 'Need uppercase, lowercase and number';
    if (form.password !== form.confirmPassword) errs.confirmPassword = 'Passwords do not match';
    return errs;
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true);
    try {
      const { confirmPassword, ...payload } = form;
      await authAPI.register(payload);
      setSuccess(true);
    } catch (err) {
      const msg = err.response?.data?.message || 'Registration failed.';
      toast.error(msg);
      if (err.response?.data?.errors) {
        const apiErrs = {};
        err.response.data.errors.forEach(e => { apiErrs[e.path] = e.msg; });
        setErrors(apiErrs);
      }
    } finally { setLoading(false); }
  };

  if (success) {
    return (
      <div className="auth-wrapper">
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 52, marginBottom: 14 }}>✅</div>
          <h2 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Registration Submitted!</h2>
          <p style={{ color: 'var(--text-2)', marginBottom: 24, lineHeight: 1.6, fontSize: 14 }}>
            Your account has been submitted successfully. Admin will review and approve it.
            You can login once approved.
          </p>
          <button className="btn btn-primary btn-full" onClick={() => navigate('/login')}>
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-wrapper">
      <div className="auth-card">
        {/* Brand */}
        <div className="auth-brand">
          <div className="auth-brand-icon">💬</div>
          <div>
            <div className="auth-brand-name">TeamChat</div>
            <div className="auth-brand-sub">Create your employee account</div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Row 1: Name + Employee ID — stacks on small screens */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Full Name *</label>
              <input
                name="name"
                className={`form-input${errors.name ? ' error' : ''}`}
                placeholder="John Doe"
                value={form.name}
                onChange={handleChange}
              />
              {errors.name && <span className="form-error">{errors.name}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">Employee ID *</label>
              <input
                name="employeeId"
                className={`form-input${errors.employeeId ? ' error' : ''}`}
                placeholder="EMP001"
                value={form.employeeId}
                onChange={handleChange}
              />
              {errors.employeeId && <span className="form-error">{errors.employeeId}</span>}
            </div>
          </div>

          {/* Email — full width */}
          <div className="form-group">
            <label className="form-label">Work Email *</label>
            <input
              type="email"
              name="email"
              className={`form-input${errors.email ? ' error' : ''}`}
              placeholder="john.doe@company.com"
              value={form.email}
              onChange={handleChange}
            />
            {errors.email && <span className="form-error">{errors.email}</span>}
          </div>

          {/* Row 2: Designation + Phone */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Designation</label>
              <input
                name="designation"
                className="form-input"
                placeholder="Manager, Analyst…"
                value={form.designation}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Phone</label>
              <input
                name="phone"
                className="form-input"
                placeholder="+91 98765 43210"
                value={form.phone}
                onChange={handleChange}
              />
            </div>
          </div>

          {/* Row 3: Password + Confirm */}
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Password *</label>
              <input
                type="password"
                name="password"
                className={`form-input${errors.password ? ' error' : ''}`}
                placeholder="Min 8 chars"
                value={form.password}
                onChange={handleChange}
              />
              {errors.password && <span className="form-error">{errors.password}</span>}
            </div>
            <div className="form-group">
              <label className="form-label">Confirm Password *</label>
              <input
                type="password"
                name="confirmPassword"
                className={`form-input${errors.confirmPassword ? ' error' : ''}`}
                placeholder="Repeat password"
                value={form.confirmPassword}
                onChange={handleChange}
              />
              {errors.confirmPassword && <span className="form-error">{errors.confirmPassword}</span>}
            </div>
          </div>

          <div className="alert alert-info" style={{ fontSize: 12, marginBottom: 14 }}>
            🔒 8+ chars · uppercase · lowercase · number required. Account needs admin approval before login.
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={loading}>
            {loading ? 'Submitting…' : 'Submit Registration →'}
          </button>
        </form>

        <div className="auth-footer">
          Already registered? <Link to="/login">Sign in here</Link>
        </div>
      </div>
    </div>
  );
}
